import { useEffect, useState, type FormEvent } from "react";
import {
  portfolio as api,
  orders as orderApi,
  type Account,
  type AccountPerformance,
  type AccountTransaction,
  type Instrument,
  type Order,
  type Position,
} from "../../api";
import { ValueChart } from "../../components/ValueChart";
import { Link, useRouter } from "../../router";
import {
  formatCurrency,
  formatCurrencyExact,
  formatUnits,
} from "./format";
import {
  instrumentLabel,
  quantityLabel,
  sideLabel,
  statusLabel,
  formatDate,
} from "../orders/format";

const TX_LABELS: Record<AccountTransaction["kind"], string> = {
  deposit: "Deposit",
  withdrawal: "Withdrawal",
  buy: "Buy",
  sell: "Sell",
  fee: "Fee",
  dividend: "Dividend",
};

export function AccountDetail({ id }: { id: number }) {
  const { navigate } = useRouter();
  const [account, setAccount] = useState<Account | null>(null);
  const [positions, setPositions] = useState<Position[] | null>(null);
  const [cashBalance, setCashBalance] = useState(0);
  const [transactions, setTransactions] = useState<AccountTransaction[] | null>(null);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cashModal, setCashModal] = useState<"deposit" | "withdrawal" | null>(null);
  const [mandateModal, setMandateModal] = useState(false);
  const [performance, setPerformance] = useState<AccountPerformance | null>(null);
  const [benchmark, setBenchmark] = useState("");
  const [instruments, setInstruments] = useState<Instrument[]>([]);

  const reload = () => {
    api
      .positions(id)
      .then((d) => {
        setAccount(d.account);
        setPositions(d.positions);
        setCashBalance(d.cash_balance);
      })
      .catch((e) => setError(e.message));
    api
      .transactions(id)
      .then((d) => setTransactions(d.transactions))
      .catch(() => setTransactions([]));
    orderApi
      .list({ account_id: id })
      .then((d) => setOrders(d.orders))
      .catch(() => {
        // Orders are secondary on this page — show an empty section rather
        // than blocking the positions view.
        setOrders([]);
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [id]);

  useEffect(() => {
    let cancelled = false;
    api
      .performance(id, benchmark || undefined)
      .then((d) => !cancelled && setPerformance(d.performance))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id, benchmark]);

  useEffect(() => {
    api
      .instruments()
      .then((d) => setInstruments(d.instruments))
      .catch(() => {});
  }, []);

  if (error) {
    return (
      <div className="page">
        <Link to="/app/portfolio" className="back-link">
          ← Portfolio
        </Link>
        <div className="banner-error">{error}</div>
      </div>
    );
  }

  if (!account || !positions) {
    return (
      <div className="page">
        <div className="muted">Loading…</div>
      </div>
    );
  }

  const securitiesValue = positions.reduce((sum, p) => sum + p.value, 0);
  const totalValue = securitiesValue + cashBalance;

  return (
    <div className="page">
      <Link to={`/app/portfolio/${account.investor_id}`} className="back-link">
        ← Investor portfolio
      </Link>

      <header className="page-head">
        <div>
          <span className="eyebrow">
            {account.type === "ask"
              ? "Aksjesparekonto"
              : account.type === "ips"
                ? "IPS"
                : "Ordinary"}{" "}
            · {account.currency}
          </span>
          <h1>{account.name}</h1>
        </div>
        <div className="page-head-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setCashModal("deposit")}
          >
            Deposit
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setCashModal("withdrawal")}
          >
            Withdraw
          </button>
          <Link
            to={`/app/orders/new?investor=${account.investor_id}&account=${account.id}`}
            className="btn btn-primary"
          >
            New order
          </Link>
        </div>
      </header>

      {positions.length === 0 && cashBalance === 0 ? (
        <div className="empty">
          <p>This account has no positions.</p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Instrument</th>
              <th>ISIN</th>
              <th className="num">Units</th>
              <th className="num">Price</th>
              <th className="num">Value</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((p) => (
              <tr key={p.id}>
                <td className="strong">{p.name}</td>
                <td className="mono">{p.isin}</td>
                <td className="num">{formatUnits(p.units)}</td>
                <td className="num">
                  {formatCurrencyExact(p.price, account.currency)}
                </td>
                <td className="num strong">
                  {formatCurrency(p.value, account.currency)}
                </td>
              </tr>
            ))}
            <tr>
              <td className="strong">Cash</td>
              <td className="mono">—</td>
              <td className="num">—</td>
              <td className="num">—</td>
              <td className="num strong">
                {formatCurrency(cashBalance, account.currency)}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4}>Account value</td>
              <td className="num">
                {formatCurrency(totalValue, account.currency)}
              </td>
            </tr>
          </tfoot>
        </table>
      )}

      {performance && performance.series.length >= 2 && (
        <>
          <div className="tab-toolbar">
            <h2 className="section-title">Performance</h2>
            <label className="muted">
              Benchmark:{" "}
              <select
                value={benchmark}
                onChange={(e) => setBenchmark(e.target.value)}
              >
                <option value="">None</option>
                {instruments
                  .filter((i) => i.currency === account.currency)
                  .map((i) => (
                    <option key={i.isin} value={i.isin}>
                      {i.name}
                    </option>
                  ))}
              </select>
            </label>
          </div>
          <div className="perf-strip">
            {Object.entries(performance.periods).map(([period, twr]) => (
              <div key={period} className="perf-figure">
                <span className="label">TWR {period}</span>
                <span
                  className={`value ${twr == null ? "" : twr > 0 ? "up" : twr < 0 ? "down" : ""}`}
                >
                  {twr == null ? "—" : `${twr > 0 ? "+" : ""}${twr}%`}
                </span>
              </div>
            ))}
          </div>
          <ValueChart
            series={performance.series}
            benchmark={performance.benchmark}
            benchmarkName={performance.benchmark_name}
            currency={account.currency}
          />
        </>
      )}

      <div className="tab-toolbar">
        <h2 className="section-title">Cash transactions</h2>
        <span className="muted">
          {account.mandate_bank_account ? (
            <>
              Funding mandate: {account.mandate_bank_account} · up to{" "}
              {formatCurrency(account.mandate_limit ?? 0, account.currency)} per
              pull ·{" "}
            </>
          ) : (
            <>No funding mandate · </>
          )}
          <button
            type="button"
            className="link-action"
            onClick={() => setMandateModal(true)}
          >
            {account.mandate_bank_account ? "Edit" : "Set up"}
          </button>
        </span>
      </div>
      {transactions === null ? (
        <div className="muted">Loading…</div>
      ) : transactions.length === 0 ? (
        <div className="empty">
          <p>No cash movements on this account yet.</p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Note</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((t) => (
              <tr key={t.id}>
                <td>{formatDate(t.occurred_at)}</td>
                <td>
                  <span className={`pill tx-${t.kind}`}>{TX_LABELS[t.kind]}</span>
                </td>
                <td>{t.note ?? "—"}</td>
                <td className={`num strong ${t.amount < 0 ? "amount-debit" : "amount-credit"}`}>
                  {t.amount > 0 ? "+" : ""}
                  {formatCurrency(t.amount, account.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2 className="section-title">Orders</h2>
      {orders === null ? (
        <div className="muted">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="empty">
          <p>No orders have been placed against this account yet.</p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Side</th>
              <th>Instrument</th>
              <th className="num">Quantity</th>
              <th>Status</th>
              <th className="num">Value</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr
                key={o.id}
                className="row-link"
                onClick={() => navigate(`/app/orders/${o.id}`)}
              >
                <td>{formatDate(o.executed_at ?? o.created_at)}</td>
                <td>
                  <span className={`pill side-${o.side}`}>{sideLabel(o.side)}</span>
                </td>
                <td>{instrumentLabel(o)}</td>
                <td className="num">
                  {quantityLabel(o.quantity_mode, o.quantity, account.currency)}
                </td>
                <td>
                  <span className={`pill status-${o.status}`}>
                    {statusLabel(o.status)}
                  </span>
                </td>
                <td className="num">
                  {o.executed_value != null
                    ? formatCurrency(o.executed_value, account.currency)
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {cashModal && (
        <CashModal
          account={account}
          kind={cashModal}
          balance={cashBalance}
          onClose={() => setCashModal(null)}
          onDone={() => {
            setCashModal(null);
            reload();
          }}
        />
      )}
      {mandateModal && (
        <MandateModal
          account={account}
          onClose={() => setMandateModal(false)}
          onDone={() => {
            setMandateModal(false);
            reload();
          }}
        />
      )}
    </div>
  );
}

function CashModal({
  account,
  kind,
  balance,
  onClose,
  onDone,
}: {
  account: Account;
  kind: "deposit" | "withdrawal";
  balance: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError("Amount must be a positive number.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.postTransaction(account.id, kind, value, note || undefined);
      onDone();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{kind === "deposit" ? "Deposit cash" : "Withdraw cash"}</h2>
        <p className="muted">
          Cash balance: {formatCurrency(balance, account.currency)}
        </p>
        <form onSubmit={onSubmit} className="card-form" noValidate>
          <label className="field">
            <span>Amount ({account.currency}) *</span>
            <input
              type="number"
              min="0"
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
              required
            />
          </label>
          <label className="field">
            <span>Note</span>
            <input
              type="text"
              value={note}
              maxLength={200}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          {error && <div className="banner-error">{error}</div>}
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {kind === "deposit" ? "Deposit" : "Withdraw"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MandateModal({
  account,
  onClose,
  onDone,
}: {
  account: Account;
  onClose: () => void;
  onDone: () => void;
}) {
  const [bankAccount, setBankAccount] = useState(account.mandate_bank_account ?? "");
  const [limit, setLimit] = useState(
    account.mandate_limit != null ? String(account.mandate_limit) : "",
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.setMandate(account.id, bankAccount.trim(), Number(limit));
      onDone();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Funding mandate</h2>
        <p className="muted">
          A debit authorization (engangsfullmakt / AvtaleGiro) lets buy orders
          pull funding from the investor's bank account instead of requiring a
          deposit first. Leave the bank account empty to remove the mandate.
        </p>
        <form onSubmit={onSubmit} className="card-form" noValidate>
          <label className="field">
            <span>Bank account</span>
            <input
              type="text"
              value={bankAccount}
              placeholder="1503.42.18871"
              maxLength={50}
              onChange={(e) => setBankAccount(e.target.value)}
              autoFocus
            />
          </label>
          <label className="field">
            <span>Per-pull limit ({account.currency})</span>
            <input
              type="number"
              min="0"
              step="any"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
            />
          </label>
          {error && <div className="banner-error">{error}</div>}
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
